"use client";

import { useMemo, useState } from "react";
import {
  addMonths,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";
import { getDefaultClassNames, type DateRange } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import {
  filterCalendarDayDisabledClass,
  filterCalendarDayHoverClass,
  filterCalendarNavButtonClass,
  filterCalendarRangeMiddleClass,
} from "./filterStyles";

const rdp = getDefaultClassNames();

const rangeCalendarClassNames = {
  root: "mx-auto w-fit",
  months: "flex flex-row gap-8",
  month: "flex flex-col gap-3",
  month_caption: "sr-only",
  nav: "hidden",
  month_grid: "w-full",
  weeks: cn(rdp.weeks, "flex flex-col gap-1.5"),
  weekdays: "flex",
  weekday:
    "flex size-10 shrink-0 items-center justify-center text-xs font-medium text-gray-500",
  week: cn(rdp.week, "flex w-full"),
  day: cn(
    rdp.day,
    "group/day relative flex h-10 w-10 items-center justify-center p-0"
  ),
  range_start: cn(
    rdp.range_start,
    filterCalendarRangeMiddleClass,
    "rounded-l-md [&.range_end]:rounded-md"
  ),
  range_end: cn(rdp.range_end, filterCalendarRangeMiddleClass, "rounded-r-md"),
  range_middle: cn(rdp.range_middle, filterCalendarRangeMiddleClass, "rounded-none"),
  outside: cn(rdp.outside, "text-gray-400"),
  disabled: "text-gray-500",
  today: "font-semibold text-gray-900",
};

interface MarketplaceRangeCalendarProps {
  selected?: DateRange;
  onSelect?: (range: DateRange | undefined) => void;
  defaultMonth?: Date;
  className?: string;
}

export function MarketplaceRangeCalendar({
  selected,
  onSelect,
  defaultMonth = new Date(),
  className,
}: MarketplaceRangeCalendarProps) {
  const [month, setMonth] = useState(() =>
    startOfMonth(selected?.from ?? defaultMonth)
  );

  const headerLabel = useMemo(() => {
    const secondMonth = addMonths(month, 1);
    return `${format(month, "MMM yyyy")} – ${format(secondMonth, "MMM yyyy")}`;
  }, [month]);

  return (
    <div className={cn("marketplace-range-calendar font-family-body", className)}>
      <div className="mb-4 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => setMonth((m) => subMonths(m, 1))}
          className={filterCalendarNavButtonClass}
          aria-label="Previous month">
          <ChevronLeft className="size-5" />
        </button>
        <span className="text-base font-semibold text-gray-900">
          {headerLabel}
        </span>
        <button
          type="button"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          className={filterCalendarNavButtonClass}
          aria-label="Next month">
          <ChevronRight className="size-5" />
        </button>
      </div>

      <Calendar
        mode="range"
        numberOfMonths={2}
        month={month}
        onMonthChange={setMonth}
        hideNavigation
        showOutsideDays
        selected={selected}
        onSelect={onSelect}
        className="w-fit bg-transparent p-0"
        classNames={rangeCalendarClassNames}
        components={{
          DayButton: ({ className, day, modifiers, ...props }) => {
            const isRangeStart = modifiers.range_start;
            const isRangeEnd = modifiers.range_end;
            const isRangeMiddle = modifiers.range_middle;
            const isRangeEdge = isRangeStart || isRangeEnd;
            const isSingleDayRange = isRangeStart && isRangeEnd;

            return (
              <button
                {...props}
                type="button"
                data-day={day.date.toLocaleDateString()}
                className={cn(
                  "relative z-10 flex h-10 cursor-pointer items-center justify-center text-sm font-medium transition-colors",
                  !isRangeMiddle &&
                    !isRangeEdge &&
                    "w-10 rounded-md",
                  !modifiers.selected &&
                    !modifiers.disabled &&
                    filterCalendarDayHoverClass,
                  isRangeMiddle &&
                    "w-full rounded-none bg-transparent text-gray-900 shadow-none hover:bg-transparent",
                  isRangeEdge &&
                    "w-full bg-brand-500 text-white shadow-none hover:bg-brand-600",
                  isSingleDayRange && "rounded-md",
                  isRangeStart &&
                    !isRangeEnd &&
                    "rounded-l-md rounded-r-none",
                  isRangeEnd &&
                    !isRangeStart &&
                    "rounded-r-md rounded-l-none",
                  modifiers.outside &&
                    !isRangeMiddle &&
                    !isRangeEdge &&
                    !modifiers.disabled &&
                    "text-gray-400",
                  modifiers.disabled && filterCalendarDayDisabledClass,
                  className
                )}
              />
            );
          },
        }}
      />
    </div>
  );
}
